import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Clock, Layers, Loader2, RefreshCw, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { api } from "@/lib/api";

/**
 * ChangeHistoryPanel — right-hand drawer showing chronological change history
 * for one entity (or a whole master when entityId is null).
 *
 * Section C upgrades:
 *   C1 — Uses server-resolved FK display strings + human labels + type-aware
 *        formatting (currency, IST dates, booleans, arrays).
 *   C2 — "No changes — values identical to existing record" label for
 *        entries flagged `values_identical`.
 *   C3 — Bulk imports (bulk_create / bulk_update) sharing the same
 *        `import_run_id` (a.k.a. job_id) collapse into ONE parent block.
 *        Filter chips: All | Manual edits | Bulk imports (with count badges).
 *
 * Props:
 *   - entityType: string (e.g. "users", "zones", "geography_states")
 *   - entityId:   string | null   (null => all changes for this master)
 *   - open, onOpenChange: sheet controls
 *   - title:      optional display title
 */
export default function ChangeHistoryPanel({
  entityType, entityId, open, onOpenChange, title,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, manual: 0, bulk: 0 });
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  const [kind, setKind] = useState("all"); // all | manual | bulk

  const load = useCallback(async () => {
    if (!entityType) return;
    setBusy(true);
    setErr(null);
    try {
      const params = { entity_type: entityType, page: 1, page_size: 50 };
      if (entityId) params.entity_id = entityId;
      if (kind !== "all") params.kind = kind;
      const { data } = await api.get("/change-history", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setCounts(data.counts || { all: 0, manual: 0, bulk: 0 });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load history");
    } finally {
      setBusy(false);
    }
  }, [entityType, entityId, kind]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Group bulk_* entries by import_run_id / job_id.
  const groups = useMemo(() => groupBulkImports(items), [items]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="change-history-panel"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#6D2C91]" />
            {title || "Change history"}
          </SheetTitle>
          <SheetDescription>
            {entityId ? "All changes for this record" : `Latest changes across ${entityType}`}
            {" "}· <span data-testid="change-history-total">{total}</span> entries in view
          </SheetDescription>
        </SheetHeader>

        {/* Filter chips */}
        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5" data-testid="change-history-filters">
            <FilterChip
              testId="chip-all"
              label="All" count={counts.all}
              active={kind === "all"} onClick={() => setKind("all")}
            />
            <FilterChip
              testId="chip-manual"
              label="Manual edits" count={counts.manual}
              active={kind === "manual"} onClick={() => setKind("manual")}
            />
            <FilterChip
              testId="chip-bulk"
              label="Bulk imports" count={counts.bulk}
              active={kind === "bulk"} onClick={() => setKind("bulk")}
            />
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={busy}
                  data-testid="change-history-refresh">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>

        {err && (
          <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
            {err}
          </div>
        )}

        {!busy && items.length === 0 && !err && (
          <div className="mt-8 text-center text-sm text-neutral-500" data-testid="change-history-empty">
            No history matches the current filter.
          </div>
        )}

        <div className="mt-3 space-y-3">
          {groups.map((g) => (
            g.kind === "bulk_group"
              ? <BulkGroup key={g.key} group={g} />
              : <HistoryEntry key={g.entry.id} entry={g.entry} />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------- Filter chip ----------
function FilterChip({ label, count, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active ? "true" : "false"}
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors " +
        (active
          ? "bg-[#6D2C91] border-[#6D2C91] text-white"
          : "bg-white border-neutral-200 text-neutral-700 hover:border-[#6D2C91] hover:text-[#6D2C91]")
      }
    >
      <span>{label}</span>
      <span
        className={
          "inline-flex min-w-[1.25rem] justify-center rounded-full px-1 text-[10px] leading-4 " +
          (active ? "bg-white/20 text-white" : "bg-neutral-100 text-neutral-600")
        }
      >
        {count ?? 0}
      </span>
    </button>
  );
}

// ---------- Grouping ----------
function groupBulkImports(items) {
  const out = [];
  const bucket = new Map(); // key -> group
  for (const it of items) {
    const isBulk = it.action === "bulk_create" || it.action === "bulk_update";
    const runId = it.import_run_id || it.job_id;
    if (isBulk && runId) {
      if (!bucket.has(runId)) {
        const g = {
          kind: "bulk_group",
          key: runId,
          import_run_id: runId,
          entries: [],
          actor_name: it.actor_name,
          actor_role: it.actor_role,
          actor_display: it.actor_display,
          ts: it.ts,
          friendly_ts: it.friendly_ts,
          entity_type: it.entity_type,
        };
        bucket.set(runId, g);
        out.push(g);
      }
      const g = bucket.get(runId);
      g.entries.push(it);
      // Track earliest ts for the group timestamp
      if (!g.ts || (it.ts && it.ts < g.ts)) {
        g.ts = it.ts; g.friendly_ts = it.friendly_ts;
      }
    } else {
      out.push({ kind: "single", entry: it });
    }
  }
  return out;
}

// ---------- Bulk group card (collapsed by default) ----------
function BulkGroup({ group }) {
  const [expanded, setExpanded] = useState(false);
  const n = group.entries.length;
  const created = group.entries.filter((e) => e.action === "bulk_create").length;
  const identical = group.entries.filter((e) => e.values_identical).length;
  // "updated" excludes identical rows so counters don't double-count.
  const updated = group.entries.filter((e) => e.action === "bulk_update" && !e.values_identical).length;
  return (
    <div
      className="rounded-xl border border-sky-200 bg-sky-50/50"
      data-testid={`bulk-group-${group.import_run_id}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-3 flex items-start gap-2"
        data-testid={`bulk-group-toggle-${group.import_run_id}`}
        aria-expanded={expanded}
      >
        <div className="mt-0.5">
          {expanded
            ? <ChevronDown className="h-4 w-4 text-sky-700" />
            : <ChevronRight className="h-4 w-4 text-sky-700" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 gap-1">
              <Layers className="h-3 w-3" /> Bulk import
            </Badge>
            <span className="text-xs text-neutral-500">
              {group.friendly_ts || new Date(group.ts).toLocaleString()}
            </span>
            <span className="text-xs text-neutral-400">·</span>
            <span className="text-xs font-semibold text-neutral-700" data-testid={`bulk-group-count-${group.import_run_id}`}>
              {n} {n === 1 ? "change" : "changes"}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-neutral-600">
            <User className="h-3 w-3" />
            {group.actor_display || group.actor_name || "system"}
            {group.actor_role && (<span className="text-neutral-400">· {group.actor_role}</span>)}
            <span className="text-neutral-400">·</span>
            {created > 0 && <span className="text-emerald-700">{created} created</span>}
            {created > 0 && updated > 0 && <span className="text-neutral-400">·</span>}
            {updated > 0 && <span className="text-amber-700">{updated} updated</span>}
            {identical > 0 && <>
              <span className="text-neutral-400">·</span>
              <span className="text-neutral-500">{identical} identical</span>
            </>}
          </div>
        </div>
      </button>

      {expanded && (
        <div
          className="border-t border-sky-200 bg-white px-3 py-2 space-y-2 rounded-b-xl"
          data-testid={`bulk-group-body-${group.import_run_id}`}
        >
          {group.entries.map((e) => (
            <HistoryEntry key={e.id} entry={e} nested />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Single entry ----------
function actionColor(action) {
  if (!action) return "bg-neutral-100 text-neutral-700";
  if (action.includes("create")) return "bg-emerald-100 text-emerald-700";
  if (action.includes("delete") || action.includes("reject")) return "bg-red-100 text-red-700";
  if (action.includes("bulk")) return "bg-sky-100 text-sky-700";
  if (action.includes("approve") || action.includes("paid")) return "bg-teal-100 text-teal-700";
  return "bg-amber-100 text-amber-700";
}

function prettyAction(action) {
  if (!action) return "";
  return action.replace(/_/g, " ");
}

function HistoryEntry({ entry, nested = false }) {
  const diff = entry.diff || {};
  const labels = entry.field_labels || {};
  const changed = Object.keys(diff);
  const identical = entry.values_identical === true;
  return (
    <div
      className={
        "rounded-xl border p-3 " +
        (nested ? "border-neutral-200 bg-neutral-50/60" : "border-neutral-200 bg-white")
      }
      data-testid={`change-history-entry-${entry.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Badge className={actionColor(entry.action) + " hover:opacity-100"}>
            {prettyAction(entry.action)}
          </Badge>
          <div className="mt-1 flex items-center gap-1 text-[11px] text-neutral-500">
            <User className="h-3 w-3" />
            {entry.actor_display || entry.actor_name || entry.actor_user_id || "system"}
            {entry.actor_role && (<span className="ml-1">· {entry.actor_role}</span>)}
          </div>
          {entry.reason && (
            <div className="text-[11px] text-neutral-500 italic mt-0.5" data-testid={`change-history-reason-${entry.id}`}>{entry.reason}</div>
          )}
        </div>
        <div className="text-[10px] font-mono text-neutral-400 whitespace-nowrap" data-testid={`change-history-ts-${entry.id}`}>
          {entry.friendly_ts || new Date(entry.ts).toLocaleString()}
        </div>
      </div>

      {changed.length > 0 ? (
        <div className="mt-2 border-t border-dashed border-neutral-200 pt-2 space-y-1">
          {changed.map((k) => (
            <FieldDiff
              key={k}
              field={k}
              label={labels[k] || k}
              before={diff[k]?.before}
              after={diff[k]?.after}
              beforeDisplay={diff[k]?.before_display}
              afterDisplay={diff[k]?.after_display}
            />
          ))}
        </div>
      ) : (
        <div className="mt-2 text-[11px] text-neutral-500" data-testid={`change-history-empty-${entry.id}`}>
          {entry.action === "create" || entry.action === "bulk_create"
            ? "Record created."
            : identical
              ? <span className="italic">No changes — values identical to existing record.</span>
              : entry.entity_ids
                ? `${entry.entity_ids.length} record(s) affected in bulk.`
                : "No field-level diff recorded."}
        </div>
      )}
    </div>
  );
}

function fmtRaw(v) {
  if (v === null || v === undefined || v === "") return <span className="text-neutral-400">—</span>;
  if (Array.isArray(v)) return v.length ? v.join(", ") : <span className="text-neutral-400">[]</span>;
  if (typeof v === "object") return <code className="text-[10px]">{JSON.stringify(v)}</code>;
  return String(v);
}

function FieldDiff({ field, label, before, after, beforeDisplay, afterDisplay }) {
  const showBefore = beforeDisplay !== undefined ? beforeDisplay : null;
  const showAfter = afterDisplay !== undefined ? afterDisplay : null;
  return (
    <div
      className="text-xs grid grid-cols-[130px,1fr,1fr] gap-2 items-baseline"
      data-testid={`change-history-field-${field}`}
    >
      <div className="font-medium text-neutral-700 truncate" title={field}>
        {label}
      </div>
      <div className="text-red-700 line-through opacity-80 truncate" title={String(before ?? "")}>
        {showBefore !== null ? (showBefore === "—" ? <span className="text-neutral-400">—</span> : showBefore) : fmtRaw(before)}
      </div>
      <div className="text-emerald-700 truncate" title={String(after ?? "")}>
        {showAfter !== null ? (showAfter === "—" ? <span className="text-neutral-400">—</span> : showAfter) : fmtRaw(after)}
      </div>
    </div>
  );
}
